import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import dayjs from 'dayjs'
import partition from 'lodash/partition'

import CaretDownOutlinedIcon from '@ant-design/icons/CaretDownOutlined'
import CaretUpOutlinedIcon from '@ant-design/icons/CaretUpOutlined'
import ExperimentOutlinedIcon from '@ant-design/icons/ExperimentOutlined'
import MedicineBoxOutlinedIcon from '@ant-design/icons/MedicineBoxOutlined'
import PillIcon from '@icons/PillIcon'

import AvatarMui from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import usePagesHistory, { PageHistoryType } from '@hooks/usePagesHistory'
import Avatar from '@components/Avatar'
import { getSizeStyle } from '@components/Avatar/Avatar.utils'

export default function PagesHistoryItemsTab({ onClose }) {
  const { history } = usePagesHistory()

  const mappedHistory = useMemo(() => {
    const [parents, children] = partition(history, (item) => !item.parentId)

    const parentIdToChildren = {}
    children.forEach((item) => {
      if (!parentIdToChildren[item.parentId]) parentIdToChildren[item.parentId] = []
      parentIdToChildren[item.parentId].push(item)
    })

    return parents.map((parent) => ({
      ...parent,
      children: parentIdToChildren[parent.id] || [],
    }))
  }, [history])

  const isEmpty = mappedHistory.length === 0

  if (isEmpty)
    return (
      <Box sx={{ p: 2 }} data-testid="page-history-empty">
        <Typography variant="h5" align="center" sx={{ color: 'text.secondary', height: 50, fontWeight: 'normal' }}>
          Nothing to see here!
        </Typography>
      </Box>
    )

  return (
    <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
      <List disablePadding data-testid="page-history-list">
        {mappedHistory.map((item, index) => (
          <HistoryItem key={item.id} divider={index < mappedHistory.length - 1} item={item} onClose={onClose} />
        ))}
      </List>
    </Box>
  )
}

function HistoryItem({ item, patient, divider = false, onClose }) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState()

  if (item.type === PageHistoryType.Patient) {
    const testId = `page-history-patient-item-${item.data.fullName}`

    const children = item.children || []
    return (
      <>
        <ListItemButton
          divider={divider}
          data-testid={testId}
          onClick={() => {
            navigate(`/app/patients/${item.data.id}`)
            onClose()
          }}
        >
          <ListItemIcon>
            <Avatar user={item.data} size="lg" />
          </ListItemIcon>
          <ListItemText
            disableTypography
            primary={
              <Typography noWrap data-testid={`${testId}-name`} sx={{ lineHeight: 1 }}>
                {item.data.fullName}
              </Typography>
            }
            secondary={
              <Stack spacing={-0.5}>
                <Typography component="span" variant="body2" data-testid={`${testId}-id`} sx={{ color: 'text.secondary' }}>
                  Patient {item.data.id}
                </Typography>
                <Typography variant="caption" data-testid={`${testId}-date`} sx={{ color: 'text.secondary' }}>
                  Last Opened: {dayjs(item.createdAt).format('MMM D, hh:mm A')}
                </Typography>
              </Stack>
            }
            sx={{ pl: 1, my: 0 }}
          />
          {children.length > 0 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation()
                setExpanded((e) => !e)
              }}
            >
              {expanded ? <CaretUpOutlinedIcon /> : <CaretDownOutlinedIcon />}
            </IconButton>
          )}
        </ListItemButton>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List disablePadding data-testid={`${testId}-children`}>
            {children.map((subitem) => (
              <HistoryItem key={subitem.id} item={subitem} patient={item} onClose={onClose} />
            ))}
          </List>
        </Collapse>
      </>
    )
  }

  if (item.type === PageHistoryType.Lab) {
    const testId = `page-history-lab-item-${item.data.id}`

    return (
      <ListItemButton
        divider={divider}
        data-testid={testId}
        onClick={() => {
          navigate(`/app/labs/${item.data.id}`)
          onClose()
        }}
      >
        <ListItemIcon>
          <AvatarMui sx={avatarStyles}>
            <ExperimentOutlinedIcon />
          </AvatarMui>
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Typography noWrap data-testid={`${testId}-name`} sx={{ lineHeight: 1 }}>
              {item.data.lab} Lab
            </Typography>
          }
          secondary={
            <Stack spacing={-0.5}>
              <Typography component="span" variant="body2" data-testid={`${testId}-id`} sx={{ color: 'text.secondary' }}>
                ID: {item.data.id} - {item.data.status}
              </Typography>
              <Typography variant="caption" data-testid={`${testId}-date`} sx={{ color: 'text.secondary' }}>
                Last Opened: {dayjs(item.createdAt).format('MMM D, hh:mm A')}
              </Typography>
            </Stack>
          }
          sx={{ pl: 1, my: 0 }}
        />
      </ListItemButton>
    )
  }

  if (item.type === PageHistoryType.Rx) {
    const testId = `page-history-rx-item-${item.data.id}`

    return (
      <ListItemButton
        divider={divider}
        data-testid={testId}
        onClick={() => {
          navigate(`/app/prescriptions/${item.data.id}`)
          onClose()
        }}
      >
        <ListItemIcon>
          <AvatarMui sx={avatarStyles}>
            <PillIcon />
          </AvatarMui>
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Typography noWrap data-testid={`${testId}-name`} sx={{ lineHeight: 1 }}>
              {item.data.drugName}
            </Typography>
          }
          secondary={
            <Stack spacing={-0.5}>
              <Typography component="span" variant="body2" data-testid={`${testId}-id`} sx={{ color: 'text.secondary' }}>
                ID: {item.data.id} - {item.data.rxStatus}
              </Typography>
              <Typography variant="caption" data-testid={`${testId}-date`} sx={{ color: 'text.secondary' }}>
                Last Opened: {dayjs(item.createdAt).format('MMM D, hh:mm A')}
              </Typography>
            </Stack>
          }
          sx={{ pl: 1, my: 0 }}
        />
      </ListItemButton>
    )
  }

  if (item.type === PageHistoryType.Encounter) {
    const testId = `page-history-encounter-item-${item.data.id}`

    return (
      <ListItemButton
        divider={divider}
        data-testid={testId}
        onClick={() => {
          navigate(`/app/patients/${patient.data.id}/encounters/${item.data.id}`)
          onClose()
        }}
      >
        <ListItemIcon>
          <AvatarMui sx={avatarStyles}>
            <MedicineBoxOutlinedIcon />
          </AvatarMui>
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Typography noWrap data-testid={`${testId}-name`} sx={{ lineHeight: 1 }}>
              {item.data.description}
            </Typography>
          }
          secondary={
            <Stack spacing={-0.5}>
              <Typography component="span" variant="body2" data-testid={`${testId}-id`} sx={{ color: 'text.secondary' }}>
                ID: {item.data.id}
              </Typography>
              <Typography variant="caption" data-testid={`${testId}-date`} sx={{ color: 'text.secondary' }}>
                Last Opened: {dayjs(item.createdAt).format('MMM D, hh:mm A')}
              </Typography>
            </Stack>
          }
          sx={{ pl: 1, my: 0 }}
        />
      </ListItemButton>
    )
  }

  return null
}

const avatarStyles = [getSizeStyle('lg'), { backgroundColor: 'background.paper', color: 'text.primary' }]
